/* eslint-disable no-restricted-syntax */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Link, Typography2, typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import MultipleSpecializationsModal from 'bundles/unified-description-page-common/components/shared/MultipleSpecializationsModal';
import { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import type { SpecializationFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCourseEntitiesFragments';
import getProductTitle from 'bundles/unified-description-page-common/utils/getProductTitle';

import _t from 'i18n!nls/xdp';

type Props = {
  specializations?: Array<SpecializationFragment> | null;
  primarySpecialization: SpecializationFragment;
  primarySpecializationLink: string;
};

const styles = {
  learnMoreButton: (theme: Theme) => css`
    padding: 0;
    margin-left: 2px;

    /* set vertical-align to unset to vertically center the button within the "Subtitles" line */
    vertical-align: unset;
    ${typography2.bodyPrimary};
    font-weight: 700;

    :hover {
      background: transparent;
    }
  `,
  specializationLink: (theme: Theme) => css`
    text-decoration-color: ${theme.palette.black[500]};
  `,
  specializationLinkText: css`
    font-weight: 600;
  `,
};

const HeroCourseDescription = ({ specializations, primarySpecialization, primarySpecializationLink }: Props) => {
  const { isModalOpen, setModalOpen } = useModal();

  if (specializations && specializations?.length > 1) {
    return (
      <React.Fragment>
        <Typography2 component="p" variant="bodyPrimary">
          <FormattedMessage
            message={_t('This course is part of multiple programs. {learnMore}')}
            learnMore={
              <Button
                variant="ghost"
                size="small"
                edgeAlign="start"
                css={styles.learnMoreButton}
                onClick={setModalOpen}
                aria-label={_t('Learn more about the programs')}
              >
                {_t('Learn more')}
              </Button>
            }
          />
        </Typography2>
        <MultipleSpecializationsModal open={isModalOpen} setOpen={setModalOpen} specializations={specializations} />
      </React.Fragment>
    );
  } else {
    return (
      <Typography2 component="p" variant="bodyPrimary">
        <FormattedMessage
          message={_t('This course is part of {specializationLink}')}
          specializationLink={
            <Link
              component={TrackedLink2}
              href={primarySpecializationLink}
              trackingName="hero_program_link"
              typographyVariant="bodyPrimary"
              css={styles.specializationLink}
            >
              <Typography2 component="span" variant="bodyPrimary" css={styles.specializationLinkText}>
                {getProductTitle(primarySpecialization.name, primarySpecialization.productVariant)}
              </Typography2>
            </Link>
          }
        />
      </Typography2>
    );
  }
};

export default HeroCourseDescription;
